/* eslint-disable max-lines */
import React, {useMemo} from 'react';
import {useTable, usePagination, useFilters, useRowSelect} from 'react-table';
import {Table, Button, Dropdown, Input, Grid, Popup} from 'semantic-ui-react';
import {Filter, DefaultOrderColumnFilter} from '../internalCommons/TableFilters';
import {invoicesPageSizesCustomOrders} from '../../../common/constants/lists';
import {Actions} from '../../../common/actions';
import {connect} from 'react-redux';
import {isCellError, isErrorInRow} from '../utils';
import {cloneDeep} from 'lodash';
import '../styles/styles.scss';

const originsTableHeaderWithProps = () => {
  return [
    {
      'Header': () => <span className='header-title'>NRO. DE ORDEN</span>,
      'accessor': 'originalOrderNumber',
      'width': 150,
      'className': 'cell-custom',
    },
    {
      'Header': () => <span className='header-title'>MODELO</span>,
      'accessor': 'model',
      'width': 250,
      'className': 'width-order-medium cell-custom',
      'Cell': ({row}) => {
        return <Popup
          content={row.original.modelDescription}
          trigger={row.original.goshi?.toUpperCase() === 'G' ?
            <b className='goshi-model'>{row.original.model}</b> :
            <b>{row.original.model}</b>
          }
          position='top center'
          size='small'
          style={{'maxWidth': '150px'}}
          inverted
        />;
      },
    },
    {
      'Header': () => <span className='header-title'>SFX</span>,
      'accessor': 'sfx',
      'width': 150,
      'className': 'width-order-tiny cell-custom',
    },
    {
      'Header': () => <span className='header-title'>COLOR</span>,
      'accessor': 'color',
      'width': 150,
      'className': 'width-order-small cell-custom',
    },
    {
      'Header': () => <span className='header-title'>COLOR EXTERNO</span>,
      'accessor': 'externalColorDescription',
      'width': 150,
      'className': 'width-order-small cell-custom',
    },
    {
      'Header': () => <span className='header-title'>COLOR INTERNO</span>,
      'accessor': 'internalColorDescription',
      'width': 150,
      'className': 'width-order-small cell-custom',
    },
    {
      'Header': () => <span className='header-title'>UNIDADES TOTALES</span>,
      'accessor': 'total',
      'width': 150,
      'className': 'width-order-tiny cell-custom',
    },
    {
      'Header': () => <span className='header-title'>FL</span>,
      'accessor': 'fl',
      'width': 100,
      'className': 'cell-custom',
    },
    {
      'Header': () => <span className='header-title'>TDEDP</span>,
      'accessor': 'tdedp',
      'width': 100,
      'className': 'cell-custom',
    },
    {
      'Header': () => <span className='header-title'>TDE</span>,
      'accessor': 'tde',
      'width': 100,
      'className': 'cell-custom',
    },
    {
      'Header': () => <span className='header-title'>CL</span>,
      'accessor': 'cl',
      'width': 100,
      'className': 'cell-custom',
    },
    {
      'Header': () => <span className='header-title'>CH</span>,
      'accessor': 'ch',
      'width': 100,
      'className': 'cell-custom',
    },
    {
      'Header': () => <span className='header-title'>CB</span>,
      'accessor': 'cb',
      'width': 100,
      'className': 'cell-custom',
    },
    {
      'Header': () => <span className='header-title'>TC</span>,
      'accessor': 'tcomercial',
      'width': 100,
      'className': 'cell-custom',
    },
    {
      'Header': () => <span className='header-title'>IT</span>,
      'accessor': 'it',
      'width': 100,
      'className': 'cell-custom',
    },
    {
      'Header': () => <span className='header-title'>TY</span>,
      'accessor': 'ty',
      'width': 100,
      'className': 'cell-custom',
    },
    {
      'Header': () => <span>MONEDA</span>,
      'accessor': 'state',
      'width': 100,
      'Cell': () => <span>USD</span>,
      'disableFilters': true,
      'className': 'cell-custom',
    },
    {
      'Header': () => <span className='header-title'>PRECIO</span>,
      'accessor': 'price',
      'width': 150,
      'Cell': ({row}) => <span>$ {row.original.price}</span>,
      'className': 'cell-custom',
    },
  ];
};

const TablePaginator = ({
  page,
  canPreviousPage,
  pageIndex,
  pageOptions,
  pageSize,
  canNextPage,
  nextPage,
  gotoPage,
  previousPage,
  setPageSize,
}) => {
  if (page.length) {
    return (
      <Grid className='pagination'>
        <Grid.Row>
          <Grid.Column width={4} textAlign='left'>
            <Button
              className='navigation-button'
              onClick={() => previousPage()}
              disabled={!canPreviousPage}
              content='Anterior'
            />
          </Grid.Column>
          <Grid.Column width={4} textAlign='right'>
            <span>páginas </span>
            <span>
              <Input
                className='pages-input'
                type='number'
                value={pageIndex + 1}
                min='1'
                max={pageOptions.length}
                onChange={(e, {value}) => {
                  const page = value ? Number(value) - 1 : 0;
                  if (page < 1 || page > pageOptions.length) {
                    e.target.value = 1;
                  }
                  gotoPage(page);
                }}
              />
            </span>{' '}
            <span>de {pageOptions.length}</span>
          </Grid.Column>
          <Grid.Column width={4} textAlign='left'>
            <Dropdown
              className='page-sizes-selection'
              selection
              upward
              compact
              value={pageSize}
              options={invoicesPageSizesCustomOrders}
              onChange={(e, {value}) => {
                setPageSize(Number(value));
              }}
            />
            <span> filas</span>
          </Grid.Column>
          <Grid.Column width={4} textAlign='right'>
            <Button
              className='navigation-button'
              onClick={() => nextPage()}
              disabled={!canNextPage}
              content='Siguiente'
            />
          </Grid.Column>
        </Grid.Row>
      </Grid>
    );
  }
  return null;
};

const OrdersTable = ({
  dataTable,
}) => {
  const clone = cloneDeep(dataTable);
  const columnsTable = originsTableHeaderWithProps();
  const data = useMemo(() => clone, []);
  const columns = useMemo(() => columnsTable, []);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    'state': {pageIndex, pageSize},
  } = useTable(
    {
      columns,
      data,
      'defaultColumn': {'Filter': DefaultOrderColumnFilter},
      'initialState': {'pageIndex': 0, 'pageSize': 100},
    },
    useFilters,
    usePagination,
    useRowSelect
  );

  return (
    <>
      <div className='table-wrapper create-lines-table'>
        <Table className='interactive' compact {...getTableProps()}>
          <Table.Header className='sticky z-index-10'>
            {headerGroups.map((headerGroup) => (
              <Table.Row key='headers' {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column, index) => (
                  <Table.HeaderCell
                    key={`title-${index}`}
                    textAlign='center'
                    className='th-semanctic-custom'
                    {...column.getHeaderProps()}>
                    {column.render('Header')}
                  </Table.HeaderCell>
                ))}
              </Table.Row>
            ))}
            {headerGroups.map((headerGroup) => (
              <Table.Row key='filters' id='filters'>
                {headerGroup.headers.map((column, index) => (
                  <Table.HeaderCell key={`filter-${index}`} textAlign='center'>
                    <Filter column={column} />
                  </Table.HeaderCell>
                ))}
              </Table.Row>
            ))}
          </Table.Header>
          <Table.Body className='body-custom' {...getTableBodyProps()}>
            {!page.length && (
              <Table.Row>
                <Table.Cell colSpan={15} textAlign='center'>
                  No hay datos disponibles
                </Table.Cell>
              </Table.Row>
            )}
            {page.length > 0 &&
              page.map((row, i) => {
                const errors = isErrorInRow(row.original);
                prepareRow(row);
                return (
                  <Table.Row
                    key={i}
                    bgcolor={errors.errorsIndexes.length > 0 && '#FFCDCD'}
                    className={`
                      row-custom cell-custom-inventory-models ${i % 2 === 0 ? 'table-row-odd' : 'table-row-even'}
                      `}
                  >
                    {row.cells.map((cell, j) => {
                      const cellError = isCellError(j, errors.errorsIndexes);
                      return (
                        <Table.Cell
                          textAlign='center'
                          bgcolor={cellError && 'red'}
                          className={cell.column.className}
                        >
                          {
                            cellError ?
                              <Popup
                                trigger={
                                  <div>
                                    {cell.render('Cell')}
                                  </div>
                                }
                                content={errors.errorsMessages[j]}
                                position='top center'
                                size='small'
                                style={{'maxWidth': '150px'}}
                                inverted
                              /> :
                              <div>
                                {cell.render('Cell')}
                              </div>
                          }
                        </Table.Cell>
                      );
                    })}
                  </Table.Row>
                );
              })}
          </Table.Body>
        </Table>
      </div>
      <TablePaginator
        className='mt-1'
        page={page}
        canPreviousPage={canPreviousPage}
        pageIndex={pageIndex}
        pageOptions={pageOptions}
        pageSize={pageSize}
        canNextPage={canNextPage}
        nextPage={nextPage}
        gotoPage={gotoPage}
        previousPage={previousPage}
        setPageSize={setPageSize}
      />
    </>
  );
};

const {activateOrigin, fetchDeleteOrigin, fetchOrigins, setOrderPrice} = Actions;

export default connect(null, {
  activateOrigin,
  fetchDeleteOrigin,
  fetchOrigins,
  setOrderPrice,
})(OrdersTable);
